import { InjectionToken } from '@angular/core';

import type { ActivityInstanceDetail } from '@api';

/**
 * An activity the queue may generate a cover for on its own: what a load or a create trigger knows about it.
 *
 * `savedText` is `null` only when a create trigger has no saved text; the server's refusal of placeholder content is
 * then the fallback.
 */
export interface CoverCandidate extends CoverResult {
  readonly activityId: string;
  readonly isOwner: boolean;
  readonly savedText: CoverCandidateText | null;
}

/** The last name and description the server accepted for an activity; never an unsaved draft. */
export interface CoverCandidateText {
  readonly name: string;
  readonly description: string;
}

/**
 * The narrow view of the cover generation queue that domains outside the product shell use.
 *
 * Every read is signal-backed, so an `OnPush` host or a `computed()` that calls it updates when the
 * queue changes.
 */
export interface CoverGenerationQueuePort {
  /**
   * Starts a generation on its own when the candidate passes the automatic eligibility rule, at most two at a time;
   * anything else is ignored.
   */
  enqueue(candidate: CoverCandidate): void;
  /** Whether a generation call for the activity is queued or running. */
  inFlight(activityId: string): boolean;
  /** The queue's last result for the activity this session, else the host's own `seed`. */
  coverOf(activityId: string, seed: CoverResult): CoverResult;
  /** Hands over a landed load or a saved framing; ignored while a started generation for the activity is still running, and when it carries an older generation record than the queue holds. */
  remember(activityId: string, result: CoverResult): void;
}

/** The cover state of one activity: what a list, detail or project response carries. */
export type CoverResult = Pick<ActivityInstanceDetail, 'cover' | 'coverGeneration'>;

/** How one generate call ended, in the terms the caller maps to a message. */
export type CoverRunOutcome =
  'aborted' | 'disabled' | 'failed' | 'inProgress' | 'notFound' | 'placeholder' | 'rereadFromServer' | 'succeeded';

/**
 * The candidate for an activity this client has just created or copied: the caller's own, with no cover and no record.
 *
 * @param activityId The id the create or copy call returned.
 * @param savedText The saved name and description, when the trigger has them.
 * @returns A candidate the eligibility rule accepts unless the saved text is a placeholder.
 */
export function createdCoverCandidate(activityId: string, savedText: CoverCandidateText | null = null): CoverCandidate {
  return { activityId, isOwner: true, cover: null, coverGeneration: null, savedText };
}

/**
 * The candidates for a batch create: one per returned id, each with the saved text of the item at the same position.
 *
 * @param ids The ids the create call returned, in the order of the items it was given.
 * @param items What the create call was given; only the name and the description are read.
 * @returns A candidate per id; an id without a matching item has no saved text.
 */
export function createdCoverCandidates(ids: readonly string[], items: readonly CoverCandidateText[]): CoverCandidate[] {
  return ids.map((id, index) => {
    const item = items[index];

    return createdCoverCandidate(id, item ? { name: item.name, description: item.description } : null);
  });
}

/**
 * The candidate for an activity whose detail has just landed, with the cover state and the saved text it carries.
 *
 * @param detail The loaded activity instance detail.
 * @returns A candidate that is the caller's own only when the detail says so.
 */
export function loadedCoverCandidate(
  detail: Pick<ActivityInstanceDetail, 'cover' | 'coverGeneration' | 'description' | 'id' | 'isOwner' | 'name'>,
): CoverCandidate {
  return {
    activityId: detail.id,
    isOwner: detail.isOwner,
    cover: detail.cover,
    coverGeneration: detail.coverGeneration,
    savedText: { name: detail.name, description: detail.description },
  };
}

/** Bound to the root `CoverGenerationQueue` in `app.config.ts`, so a dialog opened with the root injector reaches it. */
export const COVER_GENERATION_QUEUE = new InjectionToken<CoverGenerationQueuePort>('COVER_GENERATION_QUEUE');
